import React, { useState, useEffect, useContext, useCallback } from 'react';
import { UserContext } from './UserContext';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { debounce } from 'lodash';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import './WeightTracker.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const WeightTracker = () => {
  const { user } = useContext(UserContext);
  const [profileData, setProfileData] = useState(null);
  const [startingWeightInput, setStartingWeightInput] = useState('');
  const [weightInput, setWeightInput] = useState('');
  const [goalWeightInput, setGoalWeightInput] = useState('');
  const [isGoalUpdateOpen, setIsGoalUpdateOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReflection, setSelectedReflection] = useState(null);
  const [reflectionInput, setReflectionInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [screenSize, setScreenSize] = useState(window.innerWidth);
  const [filteredData, setFilteredData] = useState([]);
  const [isEditingStartingWeight, setIsEditingStartingWeight] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editedWeight, setEditedWeight] = useState('');
  const [reflectionsDisplayed, setReflectionsDisplayed] = useState(3); // Default 3 reflections

  // Convert lbs to grams
  const lbsToGrams = (lbs) => (lbs ? Math.round(lbs * 453.592) : 0);

  // Define fetchProfile function outside of useEffect
  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (user) {
        // Fetch user-specific weight data if logged in
        response = await axios.get(`https://wholeness-forward.onrender.com/api/user/${user.id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        });
      } else {
        // Fetch public weight tracking data for guests
        response = await axios.get(`https://wholeness-forward.onrender.com/api/user/public-weight-tracker`);
      }

      const data = response.data;

      // Convert grams to lbs helper function
      const gramsToLbs = (grams) => (grams ? (grams / 453.592).toFixed(1) : 'N/A');

      const startingWeightLbs = gramsToLbs(data.startingWeightG);
      const goalWeightLbs = data.goalWeightG ? gramsToLbs(data.goalWeightG) : 'N/A';

      let sortedWeightUpdates = (data.weightUpdates || [])
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
        .map(update => ({
          ...update,
          weightLbs: gramsToLbs(update.weightG),
          hasReflection: update.reflections && update.reflections.length > 0,
        }));

      const reflectionsList = sortedWeightUpdates
        .filter(update => update.reflections?.length > 0)
        .flatMap(update =>
          update.reflections.map(reflection => ({
            reflection: reflection.reflection,
            timestamp: reflection.timestamp,
            weightLbs: update.weightLbs,
          }))
        ).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

      const latestWeightUpdate = sortedWeightUpdates.length > 0 ? sortedWeightUpdates[sortedWeightUpdates.length - 1] : null;
      const previousWeightUpdate = sortedWeightUpdates.length > 1
        ? sortedWeightUpdates[sortedWeightUpdates.length - 2]
        : null;

      const currentWeightLbs = latestWeightUpdate ? latestWeightUpdate.weightLbs : startingWeightLbs;
      const weightDifferenceSinceLastUpdate = (previousWeightUpdate && latestWeightUpdate)
        ? (parseFloat(latestWeightUpdate.weightLbs) - parseFloat(previousWeightUpdate.weightLbs)).toFixed(1)
        : 'N/A';

      const weightDifferenceSinceStart = (startingWeightLbs !== 'N/A' && currentWeightLbs !== 'N/A')
        ? (parseFloat(currentWeightLbs) - parseFloat(startingWeightLbs)).toFixed(1)
        : 'N/A';

      let limit = screenSize < 768 ? 30 : 60;
      if (sortedWeightUpdates.length > limit) {
        const step = Math.ceil(sortedWeightUpdates.length / limit);
        sortedWeightUpdates = sortedWeightUpdates.filter((_, index) => index % step === 0 || sortedWeightUpdates[index].hasReflection);
      }

      setProfileData({
        username: data.username || 'Guest',
        startingWeightLbs,
        goalWeightLbs,
        startingWeightDate: data.startingWeightDate || null,
        currentWeightLbs,
        weightDifferenceSinceStart,
        weightDifferenceSinceLastUpdate,
        weightUpdates: sortedWeightUpdates,
        reflections: reflectionsList,
      });

      let weightData = [...sortedWeightUpdates];
      if (weightData.length > limit) {
        const step = Math.ceil(weightData.length / limit);
        weightData = weightData.filter((_, index) => index % step === 0);
      }

      setFilteredData(weightData);
    } catch (err) {
      console.error('❌ Error fetching profile:', err);
      setError('Error fetching profile data');
    } finally {
      setLoading(false);
    }
  }, [user, screenSize]);

  useEffect(() => {
    const handleResize = debounce(() => {
      setScreenSize(window.innerWidth);
    }, 200);  // Debounce resize event

    window.addEventListener('resize', handleResize);
    fetchProfile(); // Fetch profile data when screen size or user changes
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [user, fetchProfile]);  // Run when `user` or `screenSize` changes

  // Handle loading and error states
  if (loading) return <p className="loading-message">Loading...</p>;
  if (!profileData && !loading) return <p className="error-message">No profile data available.</p>;


// ✅ Submit Weight Update (Ensuring Grams are Sent)
const handleWeightSubmit = async (e) => {
  e.preventDefault();
  if (!weightInput.trim()) {
      setMessage('Please enter a weight.');
      return;
  }

  const weightInLbs = parseFloat(weightInput);
  const newWeightG = lbsToGrams(weightInLbs);  // ✅ Convert to grams

  console.log("📤 Submitting Weight Update:", { weightInLbs, newWeightG });

  if (isNaN(newWeightG) || newWeightG <= 0) {
      setMessage('Invalid weight input.');
      return;
  }

  // ✅ Get first groupId if user is in a group
  const userGroup = profileData.userGroups?.length > 0 ? profileData.userGroups[0]._id : null;

  try {
      await axios.put(
          `https://wholeness-forward.onrender.com/api/user/${user.id}`,
          {
              currentWeightG: newWeightG,  // ✅ Send grams
              groupId: userGroup,  // ✅ Include groupId if user is in a group
          },
          { headers: { Authorization: `Bearer ${user.token}` } }
      );

      console.log("✅ Weight update sent, now refreshing profile...");
      await fetchProfile();  // ✅ Refresh profile data
      setMessage('Weight updated successfully!');
      setWeightInput('');
  } catch (error) {
      console.error('❌ Error updating weight:', error.response?.data || error);
      setMessage('Error updating weight.');
  }
};

  // Goal weight form toggle handler
  const handleToggleGoalUpdate = () => {
    setIsGoalUpdateOpen(!isGoalUpdateOpen);
  };


  const handleGoalWeightSubmit = async (e) => {
    e.preventDefault();
    if (!goalWeightInput.trim()) {
      setMessage('Please enter a valid goal weight.');
      return;
    }
  
    const goalWeightLbs = parseFloat(goalWeightInput);
    const newGoalWeightG = lbsToGrams(goalWeightLbs); // Convert lbs to grams
  
    console.log("📤 Submitting Goal Weight Update:", { goalWeightLbs, newGoalWeightG });
  
    if (isNaN(newGoalWeightG) || newGoalWeightG <= 0) {
      setMessage('Invalid goal weight input.');
      return;
    }
  
    try {
      await axios.put(
        `https://wholeness-forward.onrender.com/api/user/${user.id}/goal-weight`, // Updated API route for goal weight
        { goalWeightG: newGoalWeightG }, // Send as grams
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
  
      fetchProfile(); // Refresh profile data
      setMessage('Goal weight updated successfully!');
      setGoalWeightInput('');
    } catch (error) {
      console.error('❌ Error updating goal weight:', error.response?.data || error);
      setMessage('Error updating goal weight.');
    }
  };
  



const handleDeleteReflection = async () => {
  try {
    const reflectionId = selectedReflection.reflectionId;

    if (!reflectionId) {
      console.error("❌ No reflection ID found.");
      setError('No reflection to delete.');
      return;
    }

    const response = await axios.delete(
      `https://wholeness-forward.onrender.com/api/reflection/${reflectionId}`,
      { headers: { Authorization: `Bearer ${user.token}` } }
    );
    console.log("✅ Reflection deleted:", response.data);

    // Refresh the profile data to reflect the deletion
    fetchProfile();  // Ensure this reloads and removes the deleted reflection
    setModalOpen(false);  // Close modal after deletion
    setMessage("Reflection deleted successfully!");
  } catch (error) {
    console.error("❌ Error deleting reflection:", error);
    setError("Error deleting reflection.");
  }
};

// ✅ Submit Starting Weight for the FIRST TIME
const handleStartingWeightSubmit = async (e) => {
  e.preventDefault();

  if (!startingWeightInput.trim()) {
    setMessage('Please enter a valid starting weight.');
    return;
  }

  const newStartingWeightG = lbsToGrams(parseFloat(startingWeightInput)); // ✅ Convert to grams

  const payload = { 
    startingWeightG: newStartingWeightG, 
    startingWeightDate: new Date().toISOString(),
  };


  try {
    await axios.put(
      `https://wholeness-forward.onrender.com/api/user/${user.id}`, // ✅ Ensure API matches backend
      payload,
      { headers: { Authorization: `Bearer ${user.token}` } }
    );

    fetchProfile(); // ✅ Refresh profile after updating
    setMessage('Starting weight set successfully!');
    setStartingWeightInput('');
  } catch (error) {
    console.error("❌ Error setting starting weight:", error.response?.data || error);
    setMessage('Error setting starting weight.');
  }
};



// ✅ Submit Starting Weight (Ensuring Grams are Sent)
const handleStartingWeightUpdate = async (e) => {
  e.preventDefault();

  if (!startingWeightInput.trim()) {
    setMessage('Please enter a valid starting weight.');
    return;
  }

  const newStartingWeightG = lbsToGrams(parseFloat(startingWeightInput)); // ✅ Convert to grams

  const payload = { 
    startingWeightG: newStartingWeightG, 
    startingWeightDate: new Date().toISOString(),
  };

  console.log("📤 Sending Starting Weight Update:", payload);

  try {
    await axios.put(
      `https://wholeness-forward.onrender.com/api/user/${user.id}/starting-weight`, // ✅ Use correct API
      payload,
      { headers: { Authorization: `Bearer ${user.token}` } }
    );

    fetchProfile(); // ✅ Refresh profile
    setMessage('Starting weight updated successfully!');
    setStartingWeightInput('');
    setIsEditingStartingWeight(false); // ✅ Close edit form
  } catch (error) {
    console.error("❌ Error updating starting weight:", error.response?.data || error);
    setMessage('Error updating starting weight.');
  }
};



// eslint-disable-next-line no-unused-vars
const handlePointClick = (event, chartElements) => {
  if (chartElements.length === 0) return;
  const index = chartElements[0].index;

  // ✅ Prevent clicking on reference lines (Goal or Starting Weight)
  if (!profileData.weightUpdates[index]) {
    console.warn("⏩ Ignoring reference lines.");
    return;
  }

  const weightUpdate = profileData.weightUpdates[index];

  if (!weightUpdate) {
    console.error("❌ No weight update found for this point.");
    return;
  }

  // ✅ Ensure `weightUpdateId` is assigned correctly
  const reflection = weightUpdate.reflections?.[0] || null;
  
  // Set selectedReflection with timestamp formatted to 'en-US'
  setSelectedReflection({
    weightUpdateId: weightUpdate._id,
    reflectionId: reflection?._id || null,
    reflection: reflection?.reflection || '',
    timestamp: reflection ? new Date(reflection.timestamp).toLocaleDateString('en-US') : new Date(weightUpdate.timestamp).toLocaleDateString('en-US')
  });

  setReflectionInput(reflection?.reflection || '');  // Set reflection text in input
  setModalOpen(true);
};



const handleSaveReflection = async () => {
  if (!selectedReflection || !selectedReflection.weightUpdateId) {
    console.error("❌ No associated weight update found for reflection.");
    setError('Error: No associated weight update found.');
    return;
  }

  try {
    let newReflectionId = selectedReflection.reflectionId;

    // Check if reflection needs to be created or updated
    if (!selectedReflection.reflectionId) {
      // Create a new reflection if no reflectionId exists
      const response = await axios.post(
        `https://wholeness-forward.onrender.com/api/reflection`,
        {
          userId: user.id,
          weightUpdateId: selectedReflection.weightUpdateId,
          reflection: reflectionInput
        },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      newReflectionId = response.data._id; // Store new reflection ID
      // Update the selected reflection object to use the new reflection ID
      setSelectedReflection(prev => ({
        ...prev,
        reflectionId: newReflectionId
      }));
    } else {
      // If reflectionId exists, update the reflection
      await axios.put(
        `https://wholeness-forward.onrender.com/api/reflection/${selectedReflection.reflectionId}`,
        { reflection: reflectionInput },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
    }

    // Update the UI state immediately after saving or updating reflection
    setProfileData(prev => {
      const updatedWeightUpdates = prev.weightUpdates.map(update =>
        update._id === selectedReflection.weightUpdateId
          ? {
              ...update,
              reflections: [
                ...update.reflections.filter(ref => ref._id !== selectedReflection.reflectionId), // Remove old reflection if updating
                {
                  _id: newReflectionId, // Use the new or existing reflection ID
                  reflection: reflectionInput,
                  timestamp: new Date().toISOString()
                }
              ],
              hasReflection: true // Set hasReflection to true to trigger chart update
            }
          : update
      );

      // Update filteredData to trigger re-render of the chart
      const updatedFilteredData = updatedWeightUpdates.map(update => ({
        ...update,
        hasReflection: update.reflections.length > 0
      }));

      // Update state with new filteredData
      setFilteredData(updatedFilteredData);

      return {
        ...prev,
        weightUpdates: updatedWeightUpdates
      };
    });

    setModalOpen(false); // Close modal after saving
    setMessage("Reflection saved successfully!");
  } catch (err) {
    console.error('❌ Error saving reflection:', err.response?.data || err);
    setError('Error saving reflection.');
    setMessage('');
  }
};


// ✅ Prepare Data for Chart
const chartLabels = filteredData.map(entry =>
  new Date(entry.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
);
const chartWeights = filteredData.map(entry => parseFloat(entry.weightLbs));

// ✅ Ensure no undefined/null values in chartWeights
const validChartWeights = chartWeights.filter(weight => weight !== null && !isNaN(weight));


// ✅ Goal Weight Data (For Reference Line)
const goalWeightData = profileData.goalWeightLbs
  ? Array(chartWeights.length).fill(parseFloat(profileData.goalWeightLbs))
  : [];

// ✅ Starting Weight Reference Line (Horizontal)
const startingWeightData = profileData.startingWeightLbs
  ? Array(chartWeights.length).fill(parseFloat(profileData.startingWeightLbs))
  : [];

// Helper function to get CSS variable values
const getCSSVar = (varName) =>
  getComputedStyle(document.documentElement).getPropertyValue(varName).trim();

// Retrieve colors dynamically
const weightLineColor = getCSSVar('--weight-line');
const weightBgColor = getCSSVar('--weight-bg');
const startingLineColor = getCSSVar('--starting-line');
const goalLineColor = getCSSVar('--goal-line');
const latestPointColor = getCSSVar('--latest-point');
const latestPointBorderColor = getCSSVar('--latest-point-border');
const reflectionColor = getCSSVar("--reflection-light");
const reflectionBorderColor = getCSSVar("--reflection-dark");
const gainPointColor = getCSSVar('--gain-point');
const lossPointColor = getCSSVar('--loss-point');

// ✅ Updated point color logic
const chartData = {
  labels: chartLabels,
  datasets: [
    {
      label: "= Weight",
      data: filteredData.map(entry => parseFloat(entry.weightLbs)), validChartWeights,
      borderColor: weightLineColor,
      backgroundColor: weightBgColor,
      borderWidth: 2,
      tension: .5,
      pointRadius: 3.5,  // ✅ Increased size for visibility
      pointBackgroundColor: filteredData.map((entry, index) => {
        if (index === filteredData.length - 1) {
          return latestPointColor; // 🔥 Latest entry
        } else if (entry.hasReflection) {
          return reflectionColor; // 🌟 Reflection point
        } else if (index > 0) {
          const prevWeight = parseFloat(filteredData[index - 1]?.weightLbs) || 0;
          const currentWeight = parseFloat(entry.weightLbs);
          return currentWeight < prevWeight ? gainPointColor : lossPointColor; // ✅ Green for loss, Red for gain
        }
        return weightLineColor; // Default color
      }),
      pointBorderColor: filteredData.map((entry, index) =>
        index === filteredData.length - 1 ? latestPointBorderColor :
        entry.hasReflection ? reflectionBorderColor :
        weightLineColor
      ),
      pointBorderWidth: 1.5,
    },
    ...(profileData.startingWeightLbs
      ? [{
          label: '= Starting',
          data: startingWeightData,
          borderColor: startingLineColor,
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
        }]
      : []),
    ...(profileData.goalWeightLbs
      ? [{
          label: '= Goal',
          data: goalWeightData,
          borderColor: goalLineColor,
          borderWidth: 2,
          borderDash: [5, 5],
          pointRadius: 0,
        }]
      : []),
  ],
};

// const chartOptions = {
//   responsive: true,
//   onClick: handlePointClick,
//   plugins: {
//     tooltip: {
//       callbacks: {
//         title: (tooltipItems) => {
//           const index = tooltipItems[0].dataIndex;
//           return new Date(filteredData[index].timestamp).toLocaleDateString('en-US', {
//             year: 'numeric',
//             month: 'short',
//             day: 'numeric',
//           });
//         },
//         label: (context) => {
//           const index = context.dataIndex;
//           const entry = filteredData[index];
//           const prevEntry = index > 0 ? filteredData[index - 1] : null;

//           if (!entry || !entry.weightLbs) {
//             return ["No Data"];
//           }

//           // ✅ Separate weight & weight change into different lines
//           const weightLine = `Weight: ${entry.weightLbs} lbs`;
          
//           let weightChangeLine = "Change: N/A";
//           if (prevEntry && prevEntry.weightLbs) {
//             const weightDiff = (parseFloat(entry.weightLbs) - parseFloat(prevEntry.weightLbs)).toFixed(1);
//             const changeIndicator = weightDiff > 0 ? "⬆️ +" : "⬇️ ";
//             weightChangeLine = `Change: ${changeIndicator}${Math.abs(weightDiff)} lbs`;
//           }

//           // ✅ Return as an array for multi-line formatting
//           return [weightLine, weightChangeLine];
//         },
//       },
//     },
//   },
// };

const handleDelete = async (weightId) => {
  if (!window.confirm("Are you sure you want to delete this weight entry?")) return;

  try {
    await axios.delete(`https://wholeness-forward.onrender.com/api/user/weight/${weightId}`, {
      headers: { Authorization: `Bearer ${user.token}` },
    });

    fetchProfile(); // Refresh list
  } catch (error) {
    console.error("❌ Error deleting weight:", error);
  }
};

const handleEdit = (entry) => {
  setEditingEntry(entry);
  setEditedWeight(entry.weightLbs); // Set current weight in input field
};

const handleSaveEdit = async (weightId, newWeight) => {
  if (!newWeight.trim()) return;

  try {
    await axios.put(
      `https://wholeness-forward.onrender.com/api/user/weight/${weightId}`,
      { newWeightG: lbsToGrams(parseFloat(newWeight)) },
      { headers: { Authorization: `Bearer ${user.token}` } }
    );

    setEditingEntry(null);
    fetchProfile(); // Refresh data
  } catch (error) {
    console.error("❌ Error updating weight:", error);
  }
};

const handleSeeMore = () => {
  setReflectionsDisplayed((prev) => prev + 5); // Load 5 more
};

const handleSeeAll = () => {
  setReflectionsDisplayed(profileData.reflections.length); // Load all
};




return (
  <div className="weight-tracker-container">
     <div className="logo-container">
      <img src="/images/weighlogo.png" alt="WeighForward Logo" className="weigh-logo" />
        </div>
    <h1 className="tracker-title2">WeighForward</h1>
    {message && <p className="message">{message}</p>}
    {error && <p className="error-message">{error}</p>}

    {/* ✅ New Flexbox Container for Two Cards */}
    <div className="tracker-cards">
      
      {/* 🟦 Card 1: Weight Info & Start Date */}
      <div className="tracker-card">
        <h3 className="card-title">Weight Information</h3>
  
        {/* ✅ Conditional Rendering: Set Starting Weight if not yet set */}
        {!profileData?.startingWeightLbs || profileData.startingWeightLbs === "N/A" ? (
          <form onSubmit={handleStartingWeightSubmit} className="weight-form">
            <h3 className="weight-form-title">Enter Starting Weight</h3>
            <input
              type="number"
              step="0.1"
              value={startingWeightInput}
              onChange={(e) => setStartingWeightInput(e.target.value)}
              placeholder="Enter starting weight (lbs)"
              className="input"
            />
            <button type="submit" className="button">Set Starting Weight</button>
          </form>
        ) : (
          <>
            <p>
              Start Weight: <strong>{profileData.startingWeightLbs}</strong> lbs
              {!isEditingStartingWeight && (
                <button 
                  className="tracker-button-tertiary" 
                  onClick={() => setIsEditingStartingWeight(true)}
                >
                  Edit
                </button>
              )}
            </p>

            {/* ✅ Show edit form only if edit mode is active */}
            {isEditingStartingWeight && (
              <form onSubmit={handleStartingWeightUpdate} className="weight-form">
                <input
                  type="number"
                  step="0.1"
                  value={startingWeightInput}
                  onChange={(e) => setStartingWeightInput(e.target.value)}
                  placeholder="Enter new starting weight (lbs)"
                  className="input"
                />
                <button type="submit" className="button">Update Start Weight</button>
                <button 
                  type="button" 
                  className="tracker-button-secondary"
                  onClick={() => setIsEditingStartingWeight(false)}
                >
                  Cancel
                </button>
              </form>
            )}
          </>
        )}

        <p>
          Start Date:{" "}
          {profileData.startingWeightDate && !isNaN(new Date(profileData.startingWeightDate).getTime())
            ? new Date(profileData.startingWeightDate).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })
            : 'N/A'}
        </p>

        {/* ✅ Current Weight & Goal Weight */}
        {profileData.currentWeightLbs && (
          <p>
            Current Weight: <strong className="current-weight">{profileData.currentWeightLbs}</strong> lbs
            {" | "}
            Goal: <strong className="goal-weight">{profileData.goalWeightLbs || 'N/A'}</strong>
          </p>
        )}

        {/* ✅ Weight Change Since Last Update */}
        {profileData.weightDifferenceSinceLastUpdate !== undefined && (
          <p>
            Change Since Last Update:{" "}
            <strong className={
              profileData.weightDifferenceSinceLastUpdate > 0 ? 'gain' :
              profileData.weightDifferenceSinceLastUpdate < 0 ? 'loss' :
              'neutral'
            }>
              {profileData.weightDifferenceSinceLastUpdate}
            </strong> lbs
          </p>
        )}

        {/* ✅ Total Weight Change */}
        {profileData.weightDifferenceSinceStart !== undefined && (
          <p>
            Total Change:{" "}
            <strong className={
              profileData.weightDifferenceSinceStart > 0 ? 'gain' :
              profileData.weightDifferenceSinceStart < 0 ? 'loss' :
              'neutral'
            }>
              {profileData.weightDifferenceSinceStart}
            </strong> lbs
          </p>
        )}
      </div>

      {/* 🟩 Card 2: Weight Update & Goal Weight */}
      <div className="tracker-card">
        

        <form onSubmit={handleWeightSubmit} className="weight-form">
          <h3 className="card-title">Update Current Weight</h3>
          <input
            type="number"
            step="0.1"
            value={weightInput}
            onChange={(e) => setWeightInput(e.target.value)}
            placeholder="Enter new weight (lbs)"
            className="input"
          />
          <button type="submit" className="button">Submit</button>
        </form>

        {/* ✅ Goal Weight Update Button */}
        <button onClick={handleToggleGoalUpdate} className="tracker-button-secondary">
          {isGoalUpdateOpen ? "Cancel Goal Update" : "Click to Update Goal"}
        </button>

        {/* ✅ Goal Weight Update Form */}
        {isGoalUpdateOpen && (
          <form onSubmit={handleGoalWeightSubmit} className="goal-form">
            <input
              type="number"
              step="0.1"
              value={goalWeightInput}
              onChange={(e) => setGoalWeightInput(e.target.value)}
              placeholder="Enter new goal (lbs)"
              className="input"
            />
            <button type="submit" className="button">Submit</button>
          </form>
        )}
      </div>
    </div>




{/* CHART SECTION */}
<div className="chart-section">
  <h3>Weight Progress</h3>
  <Line data={chartData}  />

</div>

{/* REFLECTION MODAL */}
{modalOpen && (
  <div className="modal">
    <div className="modal-content">
      <h3>Reflection for {selectedReflection.timestamp}</h3>
      <textarea
        value={reflectionInput}
        onChange={(e) => setReflectionInput(e.target.value)}  // Bind reflection text here
        placeholder="Enter your reflection"
      ></textarea>

      <div className="modal-buttons">
        {/* Conditional rendering based on if there's an existing reflection */}
        {selectedReflection.reflectionId ? (
          <>
            <button className="update-reflection" onClick={handleSaveReflection}>Update Reflection</button> {/* Button for updating existing reflection */}
            <button onClick={handleDeleteReflection} className="delete-button">
              Delete Reflection
            </button>
          </>
        ) : (
          <button onClick={handleSaveReflection}>Save Reflection</button> // Button for creating a new reflection
        )}

        <button className="modal-close" onClick={() => setModalOpen(false)}>Close</button>
      </div>
    </div>
  </div>
)}

<div className="mobile-note">
  <p>Note: Mobile users can turn phone horizontally for better graph view.</p>
  <img src="/horz-phone.svg" alt="Horizontal Phone Icon" className="mobile-icon" />
</div>
<div className="mini-void"></div>
{/* Chart Legend - Shows point meanings */}
<div className="chart-legend">
  <div className="legend-item">
    <span className="legend-point gain-point" style={{ backgroundColor: getCSSVar('--gain-point') }}></span> 
    = Gain
  </div>
  <div className="legend-item">
    <span className="legend-point loss-point" style={{ backgroundColor: getCSSVar('--loss-point') }}></span> 
    = Loss
  </div>
  <div className="legend-item">
    <span className="legend-point reflection-point" style={{ backgroundColor: getCSSVar('--reflection-light') }}></span> 
    = Reflection
  </div>
</div>


<div className="micro-void"></div>
<p className="chart-legend">Reflections can be added to any point.</p>
<div className="micro-void"></div>
<div className="weight-history-container">
  <button 
    className="button"
    onClick={() => setIsHistoryOpen(!isHistoryOpen)}
  >
    {isHistoryOpen ? "Hide Weight History ⬆️" : "View/Edit Weight History ⬇️"}
  </button>

  {isHistoryOpen && (
    <div className="weight-history-list">
      {profileData.weightUpdates.length > 0 ? (
        <ul className="weight-history">
          {profileData.weightUpdates.map((entry) => (
            <li key={entry._id} className="weight-entry">
              {/* 🔹 Date Display */}
              <span className="weight-date">
                {new Date(entry.timestamp).toLocaleDateString('en-US')}
              </span>

              {/* 🔹 Editable Weight Field */}
              {editingEntry && editingEntry._id === entry._id ? (
                <input
                  type="number"
                  step="0.1"
                  value={editedWeight}
                  onChange={(e) => setEditedWeight(e.target.value)}
                  className="edit-input"
                />
              ) : (
                <span className="weight-value">{entry.weightLbs} lbs</span>
              )}

              {/* 🔹 Action Buttons (Edit/Delete) */}
              <div className="entry-actions">
                {editingEntry && editingEntry._id === entry._id ? (
                  <>
                    <button onClick={() => handleSaveEdit(entry._id, editedWeight)}>
                      💾 Save
                    </button>
                    <button onClick={() => setEditingEntry(null)}>❌ Cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleEdit(entry)}>✏️ Edit</button>
                    <button onClick={() => handleDelete(entry._id)}>🗑️ Delete</button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="no-results">No weight history found.</p>
      )}
    </div>
  )}
</div>


{profileData.reflections && profileData.reflections.length > 0 && (
  <div className="reflections-section">
    <h3>Past Reflections</h3>
    <ul className="reflection-list">
      {profileData.reflections.slice(0, reflectionsDisplayed).map((entry, index) => (
        <li key={index} className="reflection-item">
          <span className="reflection-text">"{entry.reflection}"</span>
          <span className="reflection-meta">
            - {entry.weightLbs} lbs on {new Date(entry.timestamp).toLocaleDateString('en-US')}
          </span>
        </li>
      ))}
    </ul>

    {/* ✅ Show "See More" only if more reflections are available */}
    {reflectionsDisplayed < profileData.reflections.length && (
      <button className="tracker-button-secondary" onClick={handleSeeMore}>
        See More
      </button>
    )}

    {/* ✅ Show "See All" only if not all reflections are loaded */}
    {reflectionsDisplayed < profileData.reflections.length && (
      <button className="tracker-button-tertiary" onClick={handleSeeAll}>
        See All
      </button>
    )}
  </div>
)}

</div>
);
}

export default WeightTracker;
